import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ContactResult } from '@/lib/search-utils';
import { apiUrl } from '@/lib/api-client';
import { Button } from '@/components/ui/button';
import { Mail, UserCircle } from 'lucide-react';
import { queryKeys } from '@/lib/query-keys';
import { cn } from '@/lib/utils';
import { SearchDetailShell, SEARCH_DETAIL_CARD_CLASS } from '@/components/search/search-detail-shell';

export default function ContactSearchDetail({
  contact,
  onNavigate,
}: {
  contact: ContactResult;
  onNavigate: (id: string) => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const contactQuery = useQuery({
    queryKey: queryKeys.crmContactById(contact.id),
    queryFn: async () => {
      const r = await fetch(apiUrl(`/api/crm/contacts/${contact.id}`), { credentials: 'include' });
      if (!r.ok) return null;
      return (await r.json()) as Record<string, unknown>;
    },
    placeholderData: () => {
      const allContacts = qc.getQueryData<any[]>(queryKeys.crmContacts) || [];
      return allContacts.find((c) => c?.id === contact.id) ?? null;
    },
    retry: 1,
  });

  const c = contactQuery.data ?? contact;
  const name = (c.name ?? (c as ContactResult).name) as string;
  const email = (c.email ?? (c as ContactResult).email) as string | undefined;
  const phone = (c.phone ?? (c as ContactResult).phone) as string | undefined;
  const city = (c.city ?? (c as ContactResult).city) as string | undefined;
  const street = ((c as Record<string, unknown>).street ?? (c as { address?: string }).address) as
    | string
    | undefined;
  const ico = (c.ico ?? (c as ContactResult).ico) as string | undefined;
  const isCompany = (c.isCompany ?? (c as ContactResult).isCompany) as boolean | undefined;

  const initials =
    name
      .split(' ')
      .slice(0, 2)
      .map((n) => n[0])
      .join('')
      .toUpperCase() || '?';

  if (contactQuery.isLoading && !contactQuery.data) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-background" data-search-detail-panel>
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          <div className="flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain">
            <div className="sticky top-0 z-20 border-b border-border bg-background/95 shadow-sm backdrop-blur supports-[backdrop-filter]:bg-background/90">
              <div className="bg-background p-5 sm:p-6">
                <div className="mb-5 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between sm:gap-6">
                  <div className="flex min-w-0 flex-1 items-start gap-4">
                    <div className="h-12 w-12 shrink-0 rounded-full bg-muted motion-reduce:animate-none animate-pulse" />
                    <div className="min-w-0 flex-1 space-y-2">
                      <div className="h-7 max-w-xs rounded-md bg-muted motion-reduce:animate-none animate-pulse" />
                      <div className="h-4 max-w-sm rounded-md bg-muted/70 motion-reduce:animate-none animate-pulse" />
                    </div>
                  </div>
                  <div className="h-9 w-9 shrink-0 rounded-md border border-border bg-muted/40 motion-reduce:animate-none animate-pulse sm:h-10 sm:w-10" />
                </div>
              </div>
            </div>
            <div className="mx-auto w-full max-w-2xl flex-1 space-y-4 p-5 sm:p-6">
              <div className="h-9 w-full max-w-xs rounded-md bg-muted/50 motion-reduce:animate-none animate-pulse" />
              <div className="h-32 w-full rounded-lg border border-border bg-muted/25 motion-reduce:animate-none animate-pulse" />
              <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const titleEl = (
    <h2
      className={cn(
        'break-words font-semibold tracking-tight text-foreground',
        name.length > 42 ? 'text-lg sm:text-xl' : 'text-xl sm:text-2xl',
      )}
    >
      {name}
    </h2>
  );

  return (
    <SearchDetailShell
      title={titleEl}
      subtitle={email ?? undefined}
      headerStart={
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary text-lg font-bold text-primary-foreground">
          {initials}
        </div>
      }
      onOpenFullPage={() => onNavigate(contact.id)}
      openFullPageTitle={t('search.openFullPageTitle')}
    >
      <div className="space-y-6">
        <div className={cn(SEARCH_DETAIL_CARD_CLASS, 'space-y-4')}>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <h3 className="flex items-center gap-2 text-sm font-semibold text-foreground">
              <UserCircle className="h-4 w-4 shrink-0" />
              {t('contacts.basicInformation', 'Alapvető információk')}
            </h3>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-9 shrink-0 gap-2 font-medium"
              disabled={!email}
              onClick={() => {
                if (email) window.location.href = `mailto:${email}`;
              }}
            >
              <Mail className="h-4 w-4 shrink-0" aria-hidden />
              {t('search.sendEmailOutline')}
            </Button>
          </div>
          <div className="space-y-3 text-sm">
            <div>
              <span className="mb-1 block text-xs text-muted-foreground">{t('contacts.contactName')}</span>
              <p className="text-foreground">{name || '—'}</p>
            </div>
            <div>
              <span className="mb-1 block text-xs text-muted-foreground">{t('contacts.email')}</span>
              <p className="text-foreground">
                {email ? (
                  <a href={`mailto:${email}`} className="text-primary hover:underline">
                    {email}
                  </a>
                ) : (
                  '—'
                )}
              </p>
            </div>
            <div>
              <span className="mb-1 block text-xs text-muted-foreground">{t('contacts.mobile')}</span>
              <p className="text-foreground">
                {phone ? (
                  <a href={`tel:${phone}`} className="text-primary hover:underline">
                    {phone}
                  </a>
                ) : (
                  '—'
                )}
              </p>
            </div>
            <div>
              <span className="mb-1 block text-xs text-muted-foreground">{t('contacts.mailingCity')}</span>
              <p className="text-foreground">{city || '—'}</p>
            </div>
            <div>
              <span className="mb-1 block text-xs text-muted-foreground">{t('contacts.mailingStreet')}</span>
              <p className="text-foreground">{street || '—'}</p>
            </div>
            {ico != null && ico !== '' && (
              <div>
                <span className="mb-1 block text-xs text-muted-foreground">IČO</span>
                <p className="text-foreground tabular-nums">{ico}</p>
              </div>
            )}
          </div>
        </div>

        {isCompany !== undefined && (
          <div className={cn(SEARCH_DETAIL_CARD_CLASS, 'space-y-2')}>
            <h3 className="text-sm font-semibold text-foreground">{t('contacts.type', 'Típus')}</h3>
            <p className="text-sm text-foreground">
              {isCompany ? t('contacts.typeCompany', 'Cég') : t('contacts.typePerson', 'Személy')}
            </p>
          </div>
        )}
      </div>
    </SearchDetailShell>
  );
}
